import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth, authAxios } from './AuthContext';  // ← import authAxios
import { toast } from 'sonner';

const CartContext = createContext(null);

export const CartProvider = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [cart, setCart] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(false);

  const fetchCart = useCallback(async () => {
    if (!isAuthenticated) {
      setCart({ items: [], total: 0 });
      return;
    }
    try {
      setLoading(true);
      const response = await authAxios.get('/cart');  // ← authAxios + relative path
      setCart(response.data);
    } catch (error) {
      console.error('Failed to fetch cart:', error);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    fetchCart();
  }, [fetchCart]);

  const addToCart = async (productId, quantity = 1) => {
    if (!isAuthenticated) {
      toast.error('Please login to add items to cart');
      return false;
    }
    try {
      await authAxios.post('/cart/add', { product_id: productId, quantity });  // ← authAxios
      await fetchCart();
      toast.success('Added to cart');
      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add to cart');
      return false;
    }
  };

  const updateQuantity = async (productId, quantity) => {
    if (quantity <= 0) return removeFromCart(productId);
    try {
      await authAxios.post('/cart/add', { product_id: productId, quantity });  // ← authAxios
      await fetchCart();
      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update cart');
      return false;
    }
  };

  const removeFromCart = async (productId) => {
    try {
      await authAxios.delete(`/cart/${productId}`);  // ← authAxios
      await fetchCart();
      toast.success('Item removed');
      return true;
    } catch (error) {
      toast.error('Failed to remove item');
      return false;
    }
  };

  const clearCart = async () => {
    try {
      await authAxios.delete('/cart');  // ← authAxios
      setCart({ items: [], total: 0 });
      return true;
    } catch (error) {
      toast.error('Failed to clear cart');
      return false;
    }
  };

  const cartCount = cart.items.reduce((sum, item) => sum + item.quantity, 0);

  const value = {
    cart,
    loading,
    cartCount,
    addToCart,
    updateQuantity,
    removeFromCart,
    clearCart,
    fetchCart
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) throw new Error('useCart must be used within a CartProvider');
  return context;
};